import { tv, type VariantProps } from "@ui/lib/tv";
import { Children, type ReactNode } from "react";
import { ActionPillButton } from "./ActionPillButton";
import { ActionPillToggleButton } from "./ActionPillToggleButton";

const actionPill = tv({
  slots: {
    divider: "w-px shrink-0 bg-neutral-200",
    // Hairline edge is an `inset` ring so it paints inside the box (not 1px outside, where a
    // flush element above — e.g. a non-sticky top bar — would clip it). Drop shadows are
    // offset downward, so the pill paints nothing above its own top edge while keeping the lift.
    root: "flex items-center gap-1 rounded-full bg-white p-1 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_3px_3px_0px_rgba(0,0,0,0.1),inset_0px_0px_0px_1px_rgba(0,0,0,0.05)]",
  },
  defaultVariants: {
    size: "44",
  },
  variants: {
    size: {
      "32": { divider: "h-7" },
      "44": { divider: "h-8" },
    },
  },
});

type ActionPillVariants = VariantProps<typeof actionPill>;

export interface ActionPillProps {
  children: ReactNode;
  className?: string;
  size?: ActionPillVariants["size"];
}

function ActionPillRoot({ children, className, size }: ActionPillProps) {
  const styles = actionPill({ size });

  return (
    <div className={styles.root({ className })}>
      {Children.map(children, (child, index) => (
        <>
          {index > 0 && <div aria-hidden className={styles.divider()} />}
          {child}
        </>
      ))}
    </div>
  );
}

export const ActionPill = Object.assign(ActionPillRoot, {
  Button: ActionPillButton,
  ToggleButton: ActionPillToggleButton,
});
